import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Divider, Modal } from 'antd'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import styles from './select-label.module.scss'
import { PureButton } from '../../../../common/ui/pure-button/pure-button'
import { PureSelect } from '../../../../common/ui/pure-select/pure-select'
import { useCreateDuLabel, useDeleteLabel, useGetDuLabels } from '../../../../entities/du-labels/hooks/du-label.hooks'

type SelectLabelProps = {
  label?: string
  colClass?: string
  required?: boolean
  error?: string
  value?: number
  onChange?: (labelId: number | null) => void
}

export const SelectLabel = ({ label, colClass, required, error, onChange, value }: SelectLabelProps) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState<string>('')
  const { data: labels, isLoading } = useGetDuLabels()
  const { mutateAsync: createLabel } = useCreateDuLabel()
  const { mutateAsync: deleteLabel } = useDeleteLabel()

  const handleDelete = async (id: number) => {
    await deleteLabel(id)
    setSearchValue('')
    onChange?.(null)
  }

  const readyLabels = useMemo(() => {
    return (
      labels?.map((label) => ({
        value: label.id,
        display: label.name,
        label: (
          <div className={clsx('d-flex justify-content-between align-items-center', styles.labelWrapper)}>
            <span className={clsx(styles.labelName)}>{label.name}</span>
            <PureButton
              color="danger"
              onClick={() => {
                Modal.confirm({
                  title: t('objectsPage.deleteConfirmTitle'),
                  content: `${label.name} ${t('objectsPage.deleteDescription')}`,
                  onOk: () => handleDelete(label.id),
                })
              }}
            >
              <DeleteOutlined />
            </PureButton>
          </div>
        ),
      })) ?? []
    )
  }, [labels])

  const handleChange = (value: string) => {
    const label = labels?.find((label) => label.id === Number(value))
    if (label) {
      onChange?.(label.id)
    }
  }

  const handleSearch = (search: string) => {
    setSearchValue(search)
  }

  const handleAddLabel = async () => {
    try {
      if (searchValue) {
        await createLabel({ name: searchValue })
        toast.success(t('workspace.labelCreated'))
      }
    } catch {
      toast.error(t('workspace.labelCreateError'))
    }
  }

  return (
    <PureSelect
      colClass={colClass}
      dropdownRender={(menu) => {
        const isEmpty = readyLabels.filter((label) => label.display.toLowerCase().includes(searchValue.toLowerCase())).length === 0

        return (
          <>
            {menu}
            {isEmpty && searchValue ? (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ padding: '0 8px 4px' }}>
                  <PureButton
                    icon={<PlusOutlined />}
                    onClick={handleAddLabel}
                    style={{ textAlign: 'left' }}
                    type="text"
                    block
                  >
                    {t('common.create')} &#34;{searchValue}&#34;
                  </PureButton>
                </div>
              </>
            ) : null}
          </>
        )
      }}
      error={error}
      filterOption={false}
      label={label}
      loading={isLoading}
      notFoundContent={<div className={clsx(styles.notFoundContent)}>{t('objectsPage.noCategory')}</div>}
      onChange={handleChange}
      onSearch={handleSearch}
      optionLabelProp="display"
      options={readyLabels}
      placeholder=""
      required={required}
      searchValue={searchValue}
      value={value}
      showSearch
    />
  )
}
